"use client";
import * as S from "./styles.ts";
import { CallRatesTabs } from "./CallRatesTabs";
import { useState } from "react";
import { CallRate, CallRatesResults } from "./CallRatesResults";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";

interface ICallRatesSearchSectionComponentProps {}

type FailedResponse = {
  result: boolean;
  message: string;
};

type SuccessResponse = {
  description: string;
  country_name: string;
  provider: string;
  connection_cost: number;
  flag_url: string;
  landline_cost?: number;
  mobile_cost?: number;
  result: boolean;
  message: string;
};

const CallRatesSearchSectionComponent =
  ({}: ICallRatesSearchSectionComponentProps) => {
    const [results, setResults] = useState<(CallRate | [])[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const searchByNumber = async (number: string) => {
      const apiUrl = `/svc/call-costs/number?data=true`;
      try {
        setLoading(true);
        const response = await fetch(apiUrl, {
          method: "POST",
          body: JSON.stringify({ number: number }),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data: SuccessResponse = await response.json();
        if (!data.result) {
          throw new Error("No data found for the given number.");
        }
        const callRateResponse: CallRate = {
          description: data.description,
          countryName: data.country_name,
          provider: data.provider,
          connectionCost: data.connection_cost,
          flagUrl: data.flag_url,
          landlineCost: data.landline_cost,
          mobileCost: data.mobile_cost,
          apiType: "number",
        };
        setResults([callRateResponse, [], ...results]);
        setError("");
      } catch (error) {
        console.error(error);
        setError(
          "An error occurred while fetching the data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    const mapApiResponseToCallRates = (apiResponse: { html: string }) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(apiResponse.html, "text/html");
      const countryGrids = doc.querySelectorAll(".country-grid");

      return Array.from(countryGrids).map((grid) => {
        const typeText =
          grid.querySelector(".flag-bottom p")?.textContent?.trim() || "";
        const isLandline = typeText.toLowerCase() === "landline";

        const priceSelector = isLandline ? ".landline-price" : ".mobile-price";
        const priceRaw =
          grid.querySelector(`${priceSelector} div:nth-child(2)`)?.firstChild
            ?.textContent || "0";
        const numericPrice = parseFloat(priceRaw.replace(/[^\d.]/g, ""));

        const connRaw = grid
          .querySelector(".connection-cost div:nth-child(2)")
          ?.textContent?.trim();
        const connectionCost = connRaw === "-" ? 0 : parseFloat(connRaw || "0");

        return {
          countryName:
            grid.querySelector(".flag-top p")?.textContent?.trim() || "",
          provider: typeText, // "Landline" or "Mobile"
          description: grid.querySelector("img")?.getAttribute("alt") || "",
          flagUrl: grid.querySelector("img")?.getAttribute("src") || "",
          connectionCost: connectionCost,
          landlineCost: isLandline ? numericPrice : undefined,
          mobileCost: !isLandline ? numericPrice : undefined,
          apiType: "country" as const,
        };
      });
    };

    const searchByCountry = async (country: string) => {
      const apiUrl = `/svc/call-costs/country`;

      try {
        setLoading(true);
        const response = await fetch(apiUrl, {
          method: "POST",
          body: JSON.stringify({ country_iso: country }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        const callRates = mapApiResponseToCallRates(data);
        if (callRates.length === 0) {
          throw new Error("No data found for the given country.");
        }
        setResults((prev) => [...callRates, ...prev]);
        setError("");
      } catch (error) {
        console.error(error);
        setError(
          "An error occurred while fetching the data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    /**
     * Note: The commented out code below represents a previous implementation that fetched mobile and landline data separately.
     * The current implementation assumes the API returns both types in a single response, which is then parsed and mapped to our CallRate structure.
     * If the API structure changes or if we need to fetch them separately again, we can refer back to this code for guidance.
     */
    // const searchByCountry = async (country: string) => {
    //   const apiUrl = `/service/voip/call-cost?country=${country}`;

    //   try {
    //     setLoading(true);
    //     const response = await fetch(apiUrl, {
    //       method: "GET",
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //     });

    //     const data = await response.json();

    //     // The API structure uses data.result.mobile and data.result.fixed
    //     if (!data.result || (!data.result.mobile && !data.result.fixed)) {
    //       throw new Error("No data found for the given country.");
    //     }

    //     const { mobile, fixed } = data.result;

    //     // Helper to find the flag from our local TS file using the ISO code
    //     const getFlag = (iso: string) =>
    //       InternationalFlags.find((f) => f.country_iso === iso)
    //         ?.destinationFlagUrl || "";

    //     const newEntries: CallRate[] = [];

    //     // Process Mobile Data
    //     if (mobile) {
    //       newEntries.push({
    //         description: `Mobile rates for ${mobile.country_name}`,
    //         countryName: mobile.country_name,
    //         provider: mobile.provider_name || "Standard",
    //         connectionCost: mobile.connection_cost,
    //         flagUrl: getFlag(mobile.country_iso),
    //         // Mapping peak_cost to your cost fields
    //         landlineCost: null,
    //         mobileCost: mobile.peak_cost * 100,
    //         type: "Mobile", // Extra field to distinguish
    //         countryIso: mobile.country_iso,
    //       });
    //     }

    //     // Process Fixed (Landline) Data
    //     if (fixed) {
    //       newEntries.push({
    //         description: `Landline rates for ${fixed.country_name}`,
    //         countryName: fixed.country_name,
    //         provider: fixed.provider_name || "Standard",
    //         connectionCost: fixed.connection_cost,
    //         flagUrl: getFlag(fixed.country_iso),
    //         landlineCost: fixed.peak_cost * 100,
    //         mobileCost: null,
    //         type: "Landline", // Extra field to distinguish
    //         countryIso: fixed.country_iso,
    //       });
    //     }

    //     // Update state with the new individual entries
    //     setResults((prev) => [...prev, ...newEntries]);
    //     setError("");
    //   } catch (error) {
    //     console.error(error);
    //     setError(
    //       "An error occurred while fetching the data. Please try again later."
    //     );
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    return (
      <>
        <S.CallRatesSearchWrapper>
          <S.CallRatesSearchContainer>
            <CallRatesTabs
              onSearch={searchByNumber}
              loading={loading}
              onSearchByCountry={searchByCountry}
            />
          </S.CallRatesSearchContainer>
          <ArchStandaloneSectionComponent
            color="white"
            background="dark"
            style={{
              position: "absolute",
              top: 100,
            }}
          />
          <CallRatesResults
            error={error}
            results={results}
          />
        </S.CallRatesSearchWrapper>
      </>
    );
  };

export default CallRatesSearchSectionComponent;
